import React, { useContext, useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

import { Footer, Grid, Header, Row } from '@plurall/elo-beta'
import LoadingContainer from 'App/components/LoadingContainer/LoadingContainer'
import { SCHOOL_ADD_COORDINATOR } from 'graphql/mutations'
import { PERSON_SCHOOL_ROLES } from 'graphql/queries'
import { useForm } from 'hooks'
import UserContext from 'stores/UserStore'

import {
  ROLE_COORDINATOR_ID,
  POSITION_CLASS_STATUS_ID,
  graphqlClient,
  handleGraphQLErrorsWithToast,
} from 'utils'
import { ROUTES } from 'utils/constants'

import { coordinatorValidate as validate } from '../validations'
import { CoordinatorForm } from './CoordinatorForm'
import { LevelsContext } from './CoordinatorRoleContexts'
import RegisterCoordinatorRoleLevels from './CoordinatorRoleLevels'

const CHECK_ALL = 'checkAll'
const DEFAULT_ERROR_MESSAGE = 'Não foi possível fazer o vínculo do coordenador. Tente novamente.'

const CoordinatorData = () => (
  <RegisterCoordinatorRoleLevels>
    <RegisterCoordinatorRoleView />
  </RegisterCoordinatorRoleLevels>
)

const RegisterCoordinatorRoleView = () => {
  const userStore = useContext(UserContext)
  const [loadInitialRoles, setLoadInitialRoles] = useState(true)
  const [graphQLError, setGraphQLError] = useState('')
  const [inviteData, setInviteData] = useState(null)
  const learningLevels = [...useContext(LevelsContext)]
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate()
  const [schoolRoles, setSchoolRoles] = useState(null)
  const [schoolRolesError, setSchoolRolesError] = useState(null)
  const [loadingCoordinator, setLoadingCoordinator] = useState(true)

  useEffect(() => {
    getSchoolRoles()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const getSchoolRoles = async () => {
    try {
      const { data } = await graphqlClient().query({
        query: PERSON_SCHOOL_ROLES,
        variables: {
          schoolId: +userStore.currentSchool.id,
          positionClassStatusId: POSITION_CLASS_STATUS_ID,
        },
        fetchPolicy: 'network-only',
      })

      setSchoolRoles(data)
    } catch (e) {
      handleGraphQLErrorsWithToast(
        e,
        'Não foi possível carregar os dados do coordenador. Tente novamente mais tarde.',
      )

      setSchoolRolesError(e)
    } finally {
      setLoadingCoordinator(false)
    }
  }

  const filterLevels = levels => {
    const { checkAll, ...newLevels } = levels
    return Object.keys(newLevels).filter(value => newLevels[value] === true)
  }

  const tryAddCoordinator = async levels => {
    const selectedLevels = filterLevels(levels)
    try {
      setLoading(true)

      await graphqlClient().mutate({
        mutation: SCHOOL_ADD_COORDINATOR,
        variables: {
          schoolId: +userStore.currentSchool.id,
          levels: selectedLevels.map(level => ({
            name: level,
          })),
        },
      })
      return (window.location.href = `${ROUTES.PERFIL_DADOS_ESCOLARES}?redirect=true`)
    } catch (error) {
      return false
    } finally {
      setLoading(false)
    }
  }

  const onValidForm = async levels => {
    setGraphQLError('')

    const addCoordinator = await tryAddCoordinator(levels)
    userStore.setNeedSelectProfileCoordinator(null)
    if (!addCoordinator) {
      return setGraphQLError(DEFAULT_ERROR_MESSAGE)
    }
  }

  const fields = {}

  const { values, updateValues, handleChange, onSubmitForm, errors } = useForm(
    fields,
    onValidForm,
    validate,
  )

  const setInviteLevels = () => {
    setLoadInitialRoles(false)
    const { roles } = schoolRoles.person
    const coordinatorRole = roles.find(role => Number(role.id) === ROLE_COORDINATOR_ID)
    const coordinatorData =
      coordinatorRole &&
      coordinatorRole.levels.map(level => ({
        id: level.id.toString(),
        name: level.name,
      }))
    if (coordinatorData) {
      coordinatorData.forEach(level =>
        updateValues({
          name: level.name,
          value: true,
        }),
      )
    }
  }

  if (schoolRoles && loadInitialRoles && !schoolRolesError) {
    setInviteLevels()
  }

  const toggleAllChecked = check => {
    learningLevels.forEach(level => {
      handleChange({
        name: level.name,
        value: !check,
      })
    })

    updateValues({ name: CHECK_ALL, value: !check })
  }

  useEffect(() => {
    const initValues = () => {
      learningLevels.forEach(level =>
        updateValues({
          name: level.name,
          value: false,
        }),
      )
      updateValues({ name: CHECK_ALL, value: false })
    }

    if (!inviteData) {
      initValues()
      setInviteData(true)
    }
  }, [setInviteData, inviteData, learningLevels, updateValues, userStore, schoolRoles])

  useEffect(() => {
    const checkedItens = Object.keys(values).filter(
      key => key !== CHECK_ALL && values[key] === true,
    )
    const checkedAllValue = checkedItens ? checkedItens.length === learningLevels.length : false
    if (values.checkAll !== checkedAllValue) {
      updateValues({ name: CHECK_ALL, value: checkedAllValue })
    }
  }, [values, learningLevels, updateValues])

  if (loading) {
    return <LoadingContainer text='Salvando níveis de ensino...' fullscreen />
  }

  if (Object.keys(values).length === 0) {
    return null
  }

  if (loadingCoordinator) {
    return <LoadingContainer text='Carregando informações do coordenador...' fullscreen />
  }

  return (
    <>
      <Grid desktop={12}>
        <Row>
          <Grid desktop={3} />
          <Grid desktop={9}>
            <Header
              description={`Selecione os Níveis de Ensino que você possui vínculo na escola: ${userStore.currentSchool?.tradeName}`}
            >
              Sobre sua função de coordenador
            </Header>
            <CoordinatorForm
              onSubmitForm={onSubmitForm}
              learningLevels={learningLevels}
              errors={errors}
              graphQLError={graphQLError}
              values={values}
              handleChange={handleChange}
              loadingCoordinator={loadingCoordinator}
              toggleAllChecked={toggleAllChecked}
            />
          </Grid>
        </Row>
      </Grid>
      <Footer
        primaryButtonLabel='Continuar'
        secondaryButtonLabel='Cancelar'
        border
        onPrimaryButtonClick={onSubmitForm}
        onSecondaryButtonClick={() => navigate(ROUTES.PERFIL_DADOS_ESCOLARES)}
        isPrimaryButtonDisabled={!learningLevels.length}
        primaryButtonDataTestId='btn-confirm'
        secondaryButtonDataTestId='btn-cancel'
      />
    </>
  )
}

export { CoordinatorData }
